/**
 * Admin: global 電子交付同意書 template — live preview, editable body (記〜以上), reset/save.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConsentModal } from '../../components/FirstLoginConsent/ConsentModal';
import type { ResolvedConsentFormConfig } from '../../components/FirstLoginConsent/consentFormTypes';
import {
  buildLocalFallbackConsentConfig,
  getDefaultPrintNoticeLines,
} from '../../components/FirstLoginConsent/consentDefaults';
import {
  getConsentFormConfig,
  postConsentFormConfigReset,
  putConsentFormConfig,
} from '../../services/ConsentService';
import './css/ConsentFormSettings.css';

function mapPayloadToConfig(data: {
  sections: { title: string; lines: string[] }[];
  printNoticeLines: string[];
}): ResolvedConsentFormConfig {
  return {
    sections: data.sections,
    printNoticeLines:
      data.printNoticeLines.length >= 2
        ? [data.printNoticeLines[0], data.printNoticeLines[1]]
        : getDefaultPrintNoticeLines(),
  };
}

function buildPreview(
  draft: ResolvedConsentFormConfig,
  editing: boolean,
  editBuffer: { sectionTexts: string[]; print1: string; print2: string } | null,
): ResolvedConsentFormConfig {
  if (!editing || !editBuffer) return draft;
  return {
    sections: draft.sections.map((s, i) => ({
      title: s.title,
      lines: (editBuffer.sectionTexts[i] ?? '').split('\n'),
    })),
    printNoticeLines: [editBuffer.print1, editBuffer.print2],
  };
}

export const ConsentFormSettings: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<ResolvedConsentFormConfig | null>(null);
  const [baseline, setBaseline] = useState<ResolvedConsentFormConfig | null>(null);
  const [editing, setEditing] = useState(false);
  const [editBuffer, setEditBuffer] = useState<{
    sectionTexts: string[];
    print1: string;
    print2: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConsentFormConfig();
      const cfg = data ? mapPayloadToConfig(data) : buildLocalFallbackConsentConfig();
      setDraft(cfg);
      setBaseline(cfg);
    } catch {
      const fb = buildLocalFallbackConsentConfig();
      setDraft(fb);
      setBaseline(fb);
      setError('設定の取得に失敗しました。オフライン用の初期表示を表示しています。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const previewConfig = useMemo(() => {
    if (!draft) return null;
    return buildPreview(draft, editing, editBuffer);
  }, [draft, editing, editBuffer]);

  const startEdit = () => {
    if (!draft) return;
    setEditBuffer({
      sectionTexts: draft.sections.map((s) => s.lines.join('\n')),
      print1: draft.printNoticeLines[0] ?? '',
      print2: draft.printNoticeLines[1] ?? '',
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditBuffer(null);
  };

  const handleSave = async () => {
    if (!draft) return;
    const sectionsToSave = editing && editBuffer
      ? draft.sections.map((s, i) => ({
          title: s.title,
          lines: (editBuffer.sectionTexts[i] ?? '').split('\n'),
        }))
      : draft.sections;
    const printToSave: [string, string] =
      editing && editBuffer
        ? [editBuffer.print1, editBuffer.print2]
        : [draft.printNoticeLines[0], draft.printNoticeLines[1]];

    setSaving(true);
    setError(null);
    try {
      const res = await putConsentFormConfig({
        sections: sectionsToSave,
        printNoticeLines: printToSave,
      });
      if (!res.success || !res.data) {
        setError(res.message || '保存に失敗しました。');
        return;
      }
      const cfg = mapPayloadToConfig(res.data);
      setDraft(cfg);
      setBaseline(cfg);
      setEditing(false);
      setEditBuffer(null);
    } catch {
      setError('保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      const res = await postConsentFormConfigReset();
      if (!res.success || !res.data) {
        setError(res.message || '初期化に失敗しました。');
        return;
      }
      const cfg = mapPayloadToConfig(res.data);
      setDraft(cfg);
      setBaseline(cfg);
      setEditing(false);
      setEditBuffer(null);
    } catch {
      setError('初期化に失敗しました。');
    } finally {
      setResetting(false);
    }
  };

  if (loading || !draft || !previewConfig) {
    return (
      <div className="consent-form-settings main-content">
        <p className="consent-form-settings-loading">読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="consent-form-settings main-content">
      <div className="consent-form-settings-toolbar">
        <button type="button" className="consent-form-settings-back" onClick={onBack}>
          ← 一覧に戻る
        </button>
        <h3>電子交付同意書の設定</h3>
      </div>

      <div className="consent-form-settings-grid">
        <div className="consent-form-settings-preview-col">
          <p className="consent-form-settings-preview-label">
            {editing && editBuffer
              ? '編集（従業員に表示される内容と同じ位置で編集できます）'
              : 'プレビュー（従業員画面と同じ内容）'}
          </p>
          <ConsentModal
            variant="embedded"
            showFooterActions={false}
            empCode="000000"
            name="（プレビュー）"
            config={previewConfig}
            onAgree={() => {}}
            onDisagree={() => {}}
            inlineEdit={
              editing && editBuffer
                ? {
                    sectionTexts: editBuffer.sectionTexts,
                    onSectionTextChange: (i, v) => {
                      const next = [...editBuffer.sectionTexts];
                      next[i] = v;
                      setEditBuffer({ ...editBuffer, sectionTexts: next });
                    },
                    printLine1: editBuffer.print1,
                    printLine2: editBuffer.print2,
                    onPrintLineChange: (which, v) =>
                      setEditBuffer({
                        ...editBuffer,
                        ...(which === 1 ? { print1: v } : { print2: v }),
                      }),
                    disabled: saving || resetting,
                  }
                : undefined
            }
          />
        </div>

        <div className="consent-form-settings-panel">
          <div className="consent-form-settings-actions-top">
            <button
              type="button"
              className="consent-form-settings-btn-edit"
              onClick={editing ? cancelEdit : startEdit}
              disabled={saving || resetting}
            >
              {editing ? '編集を終了' : '編集'}
            </button>
            <button
              type="button"
              className="consent-form-settings-btn-reset"
              onClick={handleReset}
              disabled={saving || resetting}
            >
              {resetting ? '初期化中...' : '初期設定に戻す'}
            </button>
          </div>

          <p className="consent-form-settings-hint">
            「編集」で左の同意書内を直接編集します。
          </p>

          {error && (
            <p className="consent-form-settings-error" role="alert">
              {error}
            </p>
          )}

          <div className="consent-form-settings-actions-bottom">
            <button
              type="button"
              className="consent-form-settings-btn-save"
              onClick={handleSave}
              disabled={saving || resetting}
            >
              {saving ? '保存中...' : '保存'}
            </button>
            <button
              type="button"
              className="consent-form-settings-btn-cancel"
              onClick={() => {
                setDraft(baseline);
                setEditing(false);
                setEditBuffer(null);
                setError(null);
              }}
              disabled={saving || resetting}
            >
              キャンセル
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
